import { EventEmitter, Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { TopoImageLibrary } from 'topomap/topology/TopoImageLibrary';
import { TopoImageEntry } from 'topomap/topology/TopoImageEntry';
import { ConfigurationWebService } from './configuration.web.service';
import { DeviceCategory } from 'DiscoveryEngine.TS/Core';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { AuditLogSettingsDto } from 'UvExplorerServer.Dtos.TS/Configuration/AuditLogSettingsDto';
import { ServerSettingsDto } from 'UvExplorerServer.Dtos.TS/Configuration/ServerSettingsDto';
import { GetLinkedServersResultDto } from 'UvExplorerServer.Dtos.TS/Session/GetLinkedServersResultDto';
import { ResultDto, SubServerDto, SuperServerDto } from 'UvExplorerServer.Dtos.TS';
import { HashSet } from 'DiscoveryEngine.TS/Utils';
import { GetSlackInfoResultDto, } from 'UvExplorerServer.Dtos.TS/Tools';
import { SmtpSettings } from 'DiscoveryEngine.TS/Smtp';
import { IntegrationTable, Integration } from '../../DiscoveryEngine.TS/Core/Integration';
import {
	ITGlueConnectResultDto, HaloConnectResultDto, HuduConnectResultDto,
	AssetPandaConnectResultDto, JiraConnectResultDto,
	SlackConnectResultDto
} from 'UvExplorerServer.Dtos.TS/Tools';
import { Server } from 'http';


@Injectable()
export class ConfigurationService {

	private numTopoImagesLoaded: number;

	public areDeviceCategoriesLoading: boolean;
	public areDeviceCategoriesLoaded: boolean;
	public isDeviceCategoriesLoadError: boolean;

	public isIntegrationTableLoading: boolean;
	public isIntegrationTableLoaded: boolean;
	public isIntegrationTableError: boolean;

	private topoImagesLoaded: Subject<boolean>;
	public onTopoImagesLoaded: Observable<boolean>;

	public onDeviceCategoriesLoaded: EventEmitter<boolean>;

	public integrationTable: IntegrationTable;
	public onIntegrationTableLoaded: EventEmitter<IntegrationTable>;

	constructor(private configWebService: ConfigurationWebService) {

		this.numTopoImagesLoaded = 0;

		this.areDeviceCategoriesLoading = false;
		this.areDeviceCategoriesLoaded = false;
		this.isDeviceCategoriesLoadError = false;

		this.isIntegrationTableLoading = false;
		this.isIntegrationTableLoaded = false;
		this.isIntegrationTableError = false;

		this.topoImagesLoaded = new Subject<boolean>();
		this.onTopoImagesLoaded = this.topoImagesLoaded.asObservable();

		this.onDeviceCategoriesLoaded = new EventEmitter<boolean>;

		this.integrationTable = null;
		this.onIntegrationTableLoaded = new EventEmitter<IntegrationTable>();

		this.loadDeviceCategories();
		this.getIntegrationTable();
	}

	public get hasIntegrationTable(): boolean {
		return (!!this.integrationTable);
	}

	private loadDeviceCategories(): void {
		if (!this.areDeviceCategoriesLoading) {

			this.areDeviceCategoriesLoading = true;
			this.areDeviceCategoriesLoaded = false;
			this.isDeviceCategoriesLoadError = false;

			this.configWebService.getDeviceCategories().subscribe(
				(result: string[]) => {
					result.forEach((devCat: string) => {
						// Make sure all device categories are registered
						DeviceCategory.Get(devCat);
					});

					this.areDeviceCategoriesLoading = false;
					this.areDeviceCategoriesLoaded = true;
					this.isDeviceCategoriesLoadError = false;

					this.onDeviceCategoriesLoaded.emit(true);
				},
				(error: any) => {
					console.error("Failed to load device categories");
					console.error(error);

					this.areDeviceCategoriesLoading = false;
					this.areDeviceCategoriesLoaded = true;
					this.isDeviceCategoriesLoadError = true;

					this.onDeviceCategoriesLoaded.emit(false);
				});
		}
	}

	public getAuditLogSettings(): Observable<AuditLogSettingsDto> {
		return this.configWebService.getAuditLogSettings();
	}

	public getServerSettings(): Observable<ServerSettingsDto> {
		return this.configWebService.getServerSettings();
	}

	public setServerSettings(settings: ServerSettingsDto): Observable<boolean> {
		let subject = new Subject<boolean>();

		this.configWebService.setServerSettings(settings).subscribe(
			(result: boolean) => {
				subject.next(true);
			},
			(error) => {
				console.error("Failed to set server settings: " + error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public setAuditLogSettings(auditLogSettings: AuditLogSettingsDto): Observable<boolean> {		
		let subject = new Subject<boolean>();

		this.configWebService.setAuditLogSettings(auditLogSettings).subscribe(
			(result: boolean) => {
				subject.next(true);
			},
			(error) => {
				console.error("Failed to set audit log settings: " + error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public getIntegrationTable(): void {
		//return this.configWebService.getIntegrationTable();
		if (!this.isIntegrationTableLoading && !this.isIntegrationTableLoaded) {

			this.isIntegrationTableLoading = true;
			this.isIntegrationTableLoaded = false;
			this.isIntegrationTableError = false;

			this.configWebService.getIntegrationTable().subscribe(
				(result: IntegrationTable) => {
					this.integrationTable = result;

					this.isIntegrationTableLoading = false;
					this.isIntegrationTableLoaded = true;
					this.isIntegrationTableError = false;

					this.onIntegrationTableLoaded.emit(this.integrationTable);
				},
				(error: any) => {
					console.error("Failed to get integration table");
					console.error(error);

					this.isIntegrationTableLoading = false;
					this.isIntegrationTableLoaded = false;
					this.isIntegrationTableError = true;

					this.onIntegrationTableLoaded.emit(this.integrationTable);
				});
		}
	}

	private unsetIntegrationTable(): void {
		this.integrationTable = null;
		this.isIntegrationTableLoading = false;
		this.isIntegrationTableLoaded = false;
		this.isIntegrationTableError = false;
	}

	public setIntegrationTable(integrationSettings: IntegrationTable): Observable<boolean> {
		let subject = new Subject<boolean>();

		this.configWebService.setIntegrationTable(integrationSettings).subscribe(
			(result: boolean) => { 
				// Setting the integration settings does an overlay on the old settings,
				// so we need to retrieve the complete updated integration table
				this.unsetIntegrationTable();
				this.getIntegrationTable();

				subject.next(true);
			},
			(error) => {
				console.error("Failed to set integration settings: " + error);
				subject.error(error);
			});

			return subject.asObservable();
	}

	public testITGlueConnection(apiURL: string, apiKey: string): Observable<ITGlueConnectResultDto> {
		let subject = new Subject<ITGlueConnectResultDto>();

		this.configWebService.testITGlueConnect(apiURL, apiKey).subscribe(
			(result: ITGlueConnectResultDto) => {
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				subject.error("ITGlue connection failed");
			}
		)

		return subject.asObservable();
	}

	public testHaloConnection(apiURL: string, apiClientID: string, apiUsername: string, apiPassword: string): Observable<HaloConnectResultDto> {
		let subject = new Subject<HaloConnectResultDto>();

		this.configWebService.testHaloConnect(apiURL, apiClientID, apiUsername, apiPassword).subscribe(
			(result: HaloConnectResultDto) => {
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				subject.error("Halo connection failed");
			}
		)

		return subject.asObservable();
	}

	public testHuduConnection(apiURL: string, apiKey: string): Observable<HuduConnectResultDto> {
		let subject = new Subject<HuduConnectResultDto>();

		this.configWebService.testHuduConnect(apiURL, apiKey).subscribe(
			(result: HuduConnectResultDto) => {
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				subject.error("Hudu connection failed");
			}
		)

		return subject.asObservable();
	}

	public testAssetPandaConnection(serverURL: string, username: string, password: string): Observable<AssetPandaConnectResultDto> {
		let subject = new Subject<AssetPandaConnectResultDto>();

		this.configWebService.testAssetPandaConnect(serverURL, username, password).subscribe(
			(result: AssetPandaConnectResultDto) => {
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				subject.error("Asset Panda connection failed");
			}
		)

		return subject.asObservable();
	}

	public testJiraConnection(serverURL: string, username: string, apiToken: string): Observable<JiraConnectResultDto> {
		let subject = new Subject<JiraConnectResultDto>();

		this.configWebService.testJiraConnect(serverURL, username, apiToken).subscribe(
			(result: JiraConnectResultDto) => {
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				subject.error("Jira connection failed");
			}
		)

		return subject.asObservable();
	}

	public testSlackConnection(webhooks: string): Observable<SlackConnectResultDto> {
		let subject = new Subject<SlackConnectResultDto>();

		this.configWebService.testSlackConnect(webhooks).subscribe(
			(result: SlackConnectResultDto) => {
				if (result.succeeded) {
					subject.next(result);
				}
				else {
					subject.error(result.message);
				}
			},
			(error) => {
				subject.error("Slack connection failed");
			}
		)

		return subject.asObservable();
	}

	public getLinkedServers(): Observable<GetLinkedServersResultDto>{
		return this.configWebService.getLinkedServers();
	}

	public linkSuperServer(superServer: SuperServerDto): Observable<boolean> {
		return this.configWebService.linkSuperServer(superServer);
	}

	public unlinkSuperServer(): Observable<boolean> {
		return this.configWebService.unlinkSuperServer();
	}

	public syncWithSuperServer(): Observable<boolean> {
		return this.configWebService.syncWithSuperServer();
	}

	public unlinkSubServer(subServer: SubServerDto): Observable<boolean> {
		return this.configWebService.unlinkSubServer(subServer);
	}

	public testEmail(smtpSettings: SmtpSettings): Observable<ResultDto> {
		return this.configWebService.testEmail(smtpSettings);
	}
}
